import React, { useState, useCallback, useRef } from "react";
import { User, Mail, Phone, MapPin, ChevronDown, ChevronUp, X, TriangleAlert } from "lucide-react";
import { clientsApi } from "../../../services/api";
import SmsOptInCheckbox from "../../ui/SmsOptInCheckbox";

interface WizardData {
  type: string;
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  preferredLanguage: string;
  organizationName: string;
  organizationWebsite: string;
  addressLine1: string;
  addressCity: string;
  addressRegion: string;
  addressPostal: string;
  addressCountry: string;
  smsOptIn: boolean;
  tags: string[];
  // Step 2
  conflictCheckStatus: string;
  conflictCheckNotes: string;
  amlStatus: string;
  amlProvider: string;
  amlRiskScore: string;
  onboardingNotes: string;
  // Step 3
  createMatter: boolean;
  matterTitle: string;
  practiceArea: string;
  billingType: string;
  flatFeeCents: string;
  rateTableId: string | null;
  responsibleLawyerId: string;
  assignedUserIds: string[];
  matterConflictSameAsClient: boolean;
  matterConflictStatus: string;
  matterConflictNotes: string;
}

interface ClientInfoStepProps {
  data: WizardData;
  onChange: (updates: Partial<WizardData>) => void;
  errors: Record<string, string>;
}

const CLIENT_TYPES = [
  { value: "individual", label: "Individual" },
  { value: "organization", label: "Organization" },
  { value: "estate", label: "Estate" },
];

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "fr", label: "French" },
  { value: "es", label: "Spanish" },
  { value: "zh", label: "Chinese" },
  { value: "ar", label: "Arabic" },
  { value: "pt", label: "Portuguese" },
  { value: "other", label: "Other" },
];

const ClientInfoStep: React.FC<ClientInfoStepProps> = ({ data, onChange, errors }) => {
  const [showAddress, setShowAddress] = useState(!!data.addressLine1);
  const [tagInput, setTagInput] = useState("");
  const [duplicateWarning, setDuplicateWarning] = useState<string[]>([]);
  const dupCheckTimer = useRef<ReturnType<typeof setTimeout>>();

  // Check for duplicate client names as user types
  const checkDuplicateName = useCallback((name: string) => {
    clearTimeout(dupCheckTimer.current);
    if (!name.trim() || name.trim().length < 2) {
      setDuplicateWarning([]);
      return;
    }
    dupCheckTimer.current = setTimeout(async () => {
      try {
        const res = await clientsApi.list({ search: name.trim(), limit: 5 });
        const data = (res.data as any).data || res.data || [];
        const matches = data.filter(
          (c: any) => c.displayName.toLowerCase() === name.trim().toLowerCase()
        );
        setDuplicateWarning(matches.map((c: any) => c.displayName));
      } catch {
        setDuplicateWarning([]);
      }
    }, 400);
  }, []);

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !data.tags.includes(tag)) {
      onChange({ tags: [...data.tags, tag] });
    }
    setTagInput("");
  };

  const removeTag = (tag: string) => {
    onChange({ tags: data.tags.filter((t) => t !== tag) });
  };

  const isOrg = data.type === "organization";

  return (
    <div className="space-y-5">
      {/* Client Type Toggle */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Client Type</label>
        <div className="flex gap-2">
          {CLIENT_TYPES.map((ct) => (
            <button
              key={ct.value}
              type="button"
              onClick={() => onChange({ type: ct.value })}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                data.type === ct.value
                  ? "text-white shadow-sm"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
              style={data.type === ct.value ? { backgroundColor: "var(--color-primary)" } : {}}
            >
              {ct.label}
            </button>
          ))}
        </div>
      </div>

      {/* Display Name */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Display Name <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={data.displayName}
            onChange={(e) => {
              onChange({ displayName: e.target.value });
              checkDuplicateName(e.target.value);
            }}
            className={`w-full rounded-xl border pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 ${
              errors.displayName ? "border-red-300" : "border-slate-200"
            }`}
            placeholder={isOrg ? "Acme Corp" : "John Doe"}
          />
        </div>
        {errors.displayName ? (
          <p className="text-xs text-red-500 mt-1">{errors.displayName}</p>
        ) : duplicateWarning.length > 0 ? (
          <div className="flex items-start gap-2 mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
            <TriangleAlert className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              A client named <span className="font-semibold">"{duplicateWarning[0]}"</span> already exists.
              You can still create this client, but make sure it's not a duplicate.
            </p>
          </div>
        ) : (
          <p className="text-xs text-slate-400 mt-1">How this client appears across the system</p>
        )}
      </div>

      {/* Individual: First/Last Name */}
      {!isOrg && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">First Name</label>
            <input
              type="text"
              value={data.firstName}
              onChange={(e) => onChange({ firstName: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Last Name</label>
            <input
              type="text"
              value={data.lastName}
              onChange={(e) => onChange({ lastName: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500"
            />
          </div>
        </div>
      )}

      {/* Organization fields */}
      {isOrg && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Legal Name</label>
            <input
              type="text"
              value={data.organizationName}
              onChange={(e) => onChange({ organizationName: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500"
              placeholder="Full legal name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Website</label>
            <input
              type="url"
              value={data.organizationWebsite}
              onChange={(e) => onChange({ organizationWebsite: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500"
              placeholder="https://"
            />
          </div>
        </div>
      )}

      {/* Email & Phone */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="email"
              value={data.email}
              onChange={(e) => onChange({ email: e.target.value })}
              className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-emerald-500"
              placeholder="email@example.com"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="tel"
              value={data.phone}
              onChange={(e) => onChange({ phone: e.target.value })}
              className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-emerald-500"
              placeholder="(555) 123-4567"
            />
          </div>
          <SmsOptInCheckbox
            checked={data.smsOptIn}
            onChange={(val) => onChange({ smsOptIn: val })}
          />
        </div>
      </div>

      {/* Address (collapsible) */}
      <div>
        <button
          type="button"
          onClick={() => setShowAddress(!showAddress)}
          className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
        >
          <MapPin className="h-4 w-4" />
          Address
          {showAddress ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </button>
        {showAddress && (
          <div className="mt-3 space-y-3 pl-6">
            <input
              type="text"
              value={data.addressLine1}
              onChange={(e) => onChange({ addressLine1: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500"
              placeholder="Street address"
            />
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                value={data.addressCity}
                onChange={(e) => onChange({ addressCity: e.target.value })}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500"
                placeholder="City"
              />
              <input
                type="text"
                value={data.addressRegion}
                onChange={(e) => onChange({ addressRegion: e.target.value })}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500"
                placeholder="Province / State"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                value={data.addressPostal}
                onChange={(e) => onChange({ addressPostal: e.target.value })}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500"
                placeholder="Postal code"
              />
              <input
                type="text"
                value={data.addressCountry}
                onChange={(e) => onChange({ addressCountry: e.target.value })}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500"
                placeholder="Country"
              />
            </div>
          </div>
        )}
      </div>

      {/* Language + Tags row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Preferred Language</label>
          <select
            value={data.preferredLanguage}
            onChange={(e) => onChange({ preferredLanguage: e.target.value })}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500 bg-white"
          >
            {LANGUAGES.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Tags</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
              className="flex-1 rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-500"
              placeholder="Add tag..."
            />
          </div>
          {data.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {data.tags.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-light text-brand text-xs font-medium">
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)} className="hover:text-brand-hover">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientInfoStep;
